import React, { useState } from "react";
import { FaLinkedin, FaArrowRight, FaGooglePlay, FaRocket, FaHandshake, FaShieldAlt, FaStar, FaQuoteLeft } from "react-icons/fa";
import { BiSolidHotel, BiRestaurant, BiUser } from "react-icons/bi";
import { MdEventAvailable, MdOutlineInventory, MdQrCodeScanner } from "react-icons/md";
import { useNavigate, Link } from "react-router-dom";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "./Home.css";
import SEO from "../components/SEO";

const Home = () => {
  const navigate = useNavigate();
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [currentVideo, setCurrentVideo] = useState(null);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [currentImage, setCurrentImage] = useState("");

  const openVideoModal = (videoData) => {
    setCurrentVideo(videoData);
    setVideoModalOpen(true);
    document.body.style.overflow = 'hidden'; // Prevent background scroll
  };

  const closeVideoModal = () => {
    setVideoModalOpen(false);
    setCurrentVideo(null);
    document.body.style.overflow = 'auto'; // Restore scroll
  };

  const openImageModal = (imageSrc) => {
    setCurrentImage(imageSrc);
    setImageModalOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeImageModal = () => {
    setImageModalOpen(false);
    setCurrentImage("");
    document.body.style.overflow = 'auto';
  };
  
  const services = [
    {
      icon: <BiRestaurant size={40} />,
      title: "Table management",
      description: "Easy table booking and order management with QR menu",
      more: "Manage table layouts, real-time order updates, and seamless QR-based ordering system.",
    },
    {
      icon: <BiSolidHotel size={40} />,
      title: "Room management",
      description:
        "Manage all the rooms with their availability and condition at one place",
      more: "Track cleaning status, repairs, guest check-in/check-out, and reservations efficiently.",
    },
    {
      icon: <BiUser size={40} />,
      title: "Staff management",
      description: "Manage staff in one place, their salary and leaves",
      more: "Track attendance, schedule shifts, automate payroll, and manage leave requests easily.",
    },
    {
      icon: <MdOutlineInventory size={40} />,
      title: "Inventory management",
      description: "Manage inventory in one place with low stock alert",
      more: "Get real-time stock alerts, supplier tracking, and expense optimization tools.",
    },
    {
      icon: <MdEventAvailable size={40} />,
      title: "Hotel Automation",
      description: "QR menu order system, auto order and auto billing",
      more: "Self-service ordering, smart billing, and seamless guest experience automation.",
    },
    {
      icon: <MdQrCodeScanner size={40} />,
      title: "QR Menu & POS Billing",
      description: "Digital QR menu with integrated POS billing system",
      more: "Streamline orders with contactless QR menus and instant POS billing for faster service.",
    },
  ];

  const videoTutorials = [
    { id: 1, videoId: "PIlPLHBNu9Y", title: "Part 1: How to Login and Navigate the Dashboard", type: "hotel" },
    { id: 2, videoId: "kl0WSvWHPWw", title: "Part 2: Room Booking Dashboard", type: "hotel" },
    { id: 3, videoId: "uyukehGdJ2Q", title: "Part 3: Hotel Management (Room Booking Dashboard)", type: "hotel" },
    { id: 4, videoId: "AkccD9q2KHM", title: "Part 4: Room Management (Room Booking Dashboard)", type: "hotel" },
    { id: 5, videoId: "WSqPdz_eSbM", title: "Part 5: View All Bookings, Add New Booking & Analytics", type: "hotel" },
    { id: 6, videoId: "p6Sl5sLuCf0", title: "Part 6: Hotel Gallery & Photo Management", type: "hotel" },
    { id: 7, videoId: "SF9PcOnm0JQ", title: "Part 7: Restaurant Bookings, Revenue & Analytics", type: "restaurant" },
    { id: 8, videoId: "f5TKeT9HL_c", title: "Part 8: Manage Restaurants & Menus", type: "restaurant" },
    { id: 9, videoId: "49wpBq92kFY", title: "Part 9: Table Management (View, Add, Edit & Configuration)", type: "restaurant" },
    { id: 10, videoId: "PPtDMKkuweQ", title: "Part 10: Advanced Booking Operations", type: "restaurant" },
    { id: 11, videoId: "6VE8AnE6Yhs", title: "Part 11: Reports & Analytics (Daily, Monthly & Revenue Analysis)", type: "restaurant" },
    { id: 12, videoId: "ZXOuxB0YlHw", title: "Part 12: Settings (Restaurant Profile, Business Hours & Menu)", type: "restaurant" },
    { id: 13, videoId: "x6U98_qnK68", title: "Part 13: Analytics (Customer Insights, Peak Hours & Revenue Trends)", type: "restaurant" },
  ];

  const team = [
    {
      image: "/images/PrakashPhoto.jpg",
      name: "Prakash Choudhary",
      position: "Founder and CEO",
      bio: "Visionary leader driving innovation in hospitality tech.",
      linkedin: "https://www.linkedin.com/in/codepc",
      instagram: "https://www.instagram.com/pc_daukiya",
    },
    {
      image: "/images/NikhilPhoto.jpg",
      name: "Nikhil Banthia",
      position: "Founder and CFO",
      bio: "Financial strategist ensuring sustainable growth.",
      linkedin: "https://www.linkedin.com/in/nikhil-banthia",
      instagram: "https://www.instagram.com/nikhil_banthia",
    },
    {
      image: "/images/pankaj-photo2.jpg",
      name: "Pankaj Kumawat",
      position: "Full Stack Developer",
      bio: "Building scalable apps with intuitive user experiences.",
      linkedin: "https://www.linkedin.com/in/pankaj-kumawat-78395b306/",
      instagram: "https://www.instagram.com/pankajkumawat_pk/",
    },
    {
      image: "/images/piyushimg.jpg",
      name: "Piyush Purohit",
      position: "Full Stack Developer",
      bio: "Crafting beautiful and responsive interfaces.",
      linkedin: "https://www.linkedin.com/in/piyush-purohit-qs5474/",
      instagram: "https://www.instagram.com/piyush._.peeyush/",
    },
    {
      image: "/images/KritikaPhoto.jpg",
      name: "Kritika Paliwal",
      position: "Chief Graphic Designer",
      bio: "Driving brand identity with impactful and creative design solutions.",
      linkedin: "https://www.linkedin.com/in/kritika-paliwal-865b26291/",
      instagram: "https://www.instagram.com/k_paliwal15/",
    },
  ];

  const teamSliderSettings = {
    dots: true,
    infinite: true,
    speed: 550,
    slidesToShow: 2,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3200,
    pauseOnHover: true,
    adaptiveHeight: false,
    responsive: [
      { breakpoint: 1280, settings: { slidesToShow: 2 } },
      { breakpoint: 1024, settings: { slidesToShow: 2 } },
      {
        breakpoint: 900,
        settings: {
          slidesToShow: 1,
          centerMode: true,
          centerPadding: "80px",
        },
      },
      {
        breakpoint: 720,
        settings: {
          slidesToShow: 1,
          centerMode: true,
          centerPadding: "56px",
        },
      },
      {
        breakpoint: 640,
        settings: {
          slidesToShow: 1,
          centerMode: true,
          centerPadding: "40px",
        },
      },
      {
        breakpoint: 500,
        settings: {
          slidesToShow: 1,
          centerMode: true,
          centerPadding: "28px",
        },
      },
      {
        breakpoint: 420,
        settings: {
          slidesToShow: 1,
          centerMode: true,
          centerPadding: "18px",
        },
      },
    ],
    arrows: false,
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 transition-all duration-500">
      <SEO 
        title="Trivana Hospitality - Hotel & Restaurant Management Software"
        description="Transform your hospitality business with Trivana's all-in-one management platform. Streamline hotel rooms, restaurant tables, inventory tracking, staff management, and automated billing. Get QR menu ordering, POS system, and real-time analytics."
        keywords="hotel management software, restaurant management system, POS system, inventory management, table booking, room booking, hospitality software, hotel automation, restaurant automation, billing software, QR menu, staff management, cloud-based hotel management"
        url="/"
      />
      {/* Hero Section */}
      <section id="home" className="pt-0 pb-0 px-0">
        <div className="w-full">
          {/* Content Below Image */}
          <div className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-black shadow-2xl overflow-hidden">
            <div className="relative z-10 py-8 sm:py-12 px-5 sm:px-10 md:px-12 max-w-7xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
                {/* Left Column - Main Heading */}
                <div className="animate-fade-in-up">
                  <h1 className="text-3xl xs:text-4xl sm:text-5xl md:text-6xl font-extrabold leading-tight mb-5" style={{fontFamily: 'Montserrat, sans-serif'}}>
                    <span className="text-white block mb-2">The Smart</span>
                    <span className="text-white block mb-2">solution for all</span>
                    <span className="text-white block">your <span className="text-[#4299f5]">Hotel</span></span>
                    <span className="text-[#4299f5]">& Restaurant<span className="text-white block">needs</span></span>
                  </h1>
                  
                  <div className="animate-fade-in-up delay-300 mt-12">
                    <a
                      href="/pricing"
                      className="inline-flex items-center gap-2 px-6 py-3 bg-[#4299f5] text-white font-bold rounded-full hover:bg-[#3b8ae0] transition-colors duration-200 shadow-lg hover:shadow-xl text-base"
                      style={{fontFamily: 'Montserrat, sans-serif'}}
                    >
                      Get Started
                      <FaArrowRight className="text-base" />
                    </a>
                  </div>
                </div>

                {/* Right Column – Apps & Ecosystem */}
                <div className="animate-fade-in-up delay-150 space-y-3">

                  {/* ── Apps card ── */}
                  <div className="bg-white/10 backdrop-blur-md rounded-2xl p-4 border border-white/15 shadow-lg">
                    <p className="text-[10px] font-semibold uppercase tracking-widest text-[#4299f5] mb-3">
                      📱 Download Our Apps
                    </p>

                    {/* App rows */}
                    <div className="space-y-2.5">
                      {/* Trivana Merchant */}
                      <a
                        href="https://play.google.com/store/apps/details?id=com.trivanahospitality.trivana_merchant_app"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-between gap-3 bg-white/8 hover:bg-white/15 border border-white/10 hover:border-[#4299f5]/50 rounded-xl px-3 py-2.5 transition-all duration-300 group hover:-translate-y-0.5 hover:shadow-lg"
                      >
                        <div className="flex items-center gap-2.5 min-w-0">
                          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#4299f5] to-[#2d7dd1] flex items-center justify-center flex-shrink-0 shadow-md group-hover:scale-110 transition-transform duration-300">
                            <FaGooglePlay className="text-white text-sm" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-white font-bold text-[12px] leading-tight truncate">Trivana Merchant</p>
                            <p className="text-white/55 text-[10px] truncate">Owner & business manager</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5 bg-white rounded-lg px-2.5 py-1.5 flex-shrink-0 shadow group-hover:bg-[#4299f5] transition-colors duration-300">
                          <FaGooglePlay className="text-[#4299f5] group-hover:text-white text-[10px] transition-colors" />
                          <span className="text-[10px] font-bold text-gray-800 group-hover:text-white transition-colors whitespace-nowrap">Play Store</span>
                        </div>
                      </a>

                      {/* Trivana Mobile POS */}
                      <a
                        href="https://play.google.com/store/apps/details?id=com.trivana.mobilepos&pcampaignid=web_share"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-between gap-3 bg-white/8 hover:bg-white/15 border border-white/10 hover:border-[#4299f5]/50 rounded-xl px-3 py-2.5 transition-all duration-300 group hover:-translate-y-0.5 hover:shadow-lg"
                      >
                        <div className="flex items-center gap-2.5 min-w-0">
                          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center flex-shrink-0 shadow-md group-hover:scale-110 transition-transform duration-300">
                            <FaGooglePlay className="text-white text-sm" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-white font-bold text-[12px] leading-tight truncate">Trivana Mobile POS</p>
                            <p className="text-white/55 text-[10px] truncate">Smart billing & point-of-sale</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5 bg-white rounded-lg px-2.5 py-1.5 flex-shrink-0 shadow group-hover:bg-emerald-500 transition-colors duration-300">
                          <FaGooglePlay className="text-emerald-600 group-hover:text-white text-[10px] transition-colors" />
                          <span className="text-[10px] font-bold text-gray-800 group-hover:text-white transition-colors whitespace-nowrap">Play Store</span>
                        </div>
                      </a>
                    </div>
                  </div>



                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Company Features Section */}
      <section className="py-16 px-4 bg-gradient-to-br from-white via-blue-50/30 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-extrabold mb-4 text-gray-900" style={{fontFamily: 'Montserrat, sans-serif'}}>
              Get to know Trivana's<br />unique features
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Discover what makes Trivana the perfect choice for modern hospitality businesses
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="group relative bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 border-2 border-transparent hover:border-[#4299f5]">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#4299f5] to-[#2d7dd1] rounded-t-2xl"></div>
              <div className="flex justify-center mb-6">
                <div className="w-20 h-20 bg-gradient-to-br from-[#4299f5] to-[#2d7dd1] rounded-2xl flex items-center justify-center transform group-hover:scale-110 transition-transform duration-300 shadow-lg">
                  <FaRocket className="text-4xl text-white" />
                </div>
              </div>
              <h3 className="text-2xl font-bold mb-4 text-center text-gray-900" style={{fontFamily: 'Montserrat, sans-serif'}}>
                All-in-one solution
              </h3>
              <p className="text-gray-600 text-base leading-relaxed text-center">
                Complete hospitality management system covering hotels, restaurants, and cafes. From booking to billing, we've got everything you need in one platform.
              </p>
            </div>

            {/* Feature 2 */}
            <div className="group relative bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 border-2 border-transparent hover:border-[#4299f5]">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#4299f5] to-[#2d7dd1] rounded-t-2xl"></div>
              <div className="flex justify-center mb-6">
                <div className="w-20 h-20 bg-gradient-to-br from-[#4299f5] to-[#2d7dd1] rounded-2xl flex items-center justify-center transform group-hover:scale-110 transition-transform duration-300 shadow-lg">
                  <FaHandshake className="text-4xl text-white" />
                </div>
              </div>
              <h3 className="text-2xl font-bold mb-4 text-center text-gray-900" style={{fontFamily: 'Montserrat, sans-serif'}}>
                Customer-centric approach
              </h3>
              <p className="text-gray-600 text-base leading-relaxed text-center">
                Built with your needs in mind. Our flexible pricing plans and customizable features ensure you only pay for what you need and use.
              </p>
            </div>

            {/* Feature 3 */}
            <div className="group relative bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 border-2 border-transparent hover:border-[#4299f5]">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#4299f5] to-[#2d7dd1] rounded-t-2xl"></div>
              <div className="flex justify-center mb-6">
                <div className="w-20 h-20 bg-gradient-to-br from-[#4299f5] to-[#2d7dd1] rounded-2xl flex items-center justify-center transform group-hover:scale-110 transition-transform duration-300 shadow-lg">
                  <FaShieldAlt className="text-4xl text-white" />
                </div>
              </div>
              <h3 className="text-2xl font-bold mb-4 text-center text-gray-900" style={{fontFamily: 'Montserrat, sans-serif'}}>
                Reliability & security
              </h3>
              <p className="text-gray-600 text-base leading-relaxed text-center">
                Your business data is safe with us. We provide 24/7 uptime, secure cloud storage, and regular backups to ensure your operations never stop.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section
        id="services"
        className="py-12 px-4 bg-gradient-to-br from-gray-50 via-blue-50/30 to-gray-50"
      >
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-10 text-gray-900" style={{fontFamily: 'Montserrat, sans-serif'}}>
            Our Services
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <div 
                key={index} 
                onClick={() => navigate('/features')}
                className="group bg-white rounded-xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer border-2 border-transparent hover:border-[#4299f5] transform hover:-translate-y-1"
              >
                {/* Icon */}
                <div className="flex justify-center mb-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-blue-200 rounded-full flex items-center justify-center shadow-md group-hover:shadow-lg group-hover:scale-110 transition-all duration-300">
                    <div className="text-gray-800">
                      {service.icon}
                    </div>
                  </div>
                </div>
                
                {/* Title */}
                <h3 className="text-xl font-bold text-center mb-3 text-gray-900 group-hover:text-[#4299f5] transition-colors" style={{fontFamily: 'Montserrat, sans-serif'}}>
                  {service.title}
                </h3>
                
                {/* Description */}
                <p className="text-gray-600 text-center text-sm leading-relaxed">
                  {service.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-12 px-4 bg-gradient-to-br from-yellow-50 via-white to-yellow-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-10 text-gray-900" style={{fontFamily: 'Montserrat, sans-serif'}}>
            Some of our happy customers
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Testimonial 1 */}
            <div className="bg-white rounded-xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 relative">
              <div className="absolute top-4 right-4 text-[#4299f5] opacity-20">
                <FaQuoteLeft className="text-3xl" />
              </div>
              
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-[#4299f5] to-[#2d7dd1] rounded-full flex items-center justify-center text-white text-lg font-bold">
                  RS
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 text-base" style={{fontFamily: 'Montserrat, sans-serif'}}>Dhanna Ram Daukiya</h4>
                  <p className="text-gray-600 text-xs">Hotel (NKP) Owner, Jodhpur</p>
                </div>
              </div>
              
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <FaStar key={i} className="text-yellow-400" />
                ))}
              </div>
              
              <p className="text-gray-700 leading-relaxed italic">
                "The POS system and QR menu feature have increased our order accuracy by 95%. Our customers love the contactless ordering. Best investment we've made!"
              </p>
            </div>

            {/* Testimonial 2 */}
            <div className="bg-white rounded-xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 relative">
              <div className="absolute top-4 right-4 text-[#4299f5] opacity-20">
                <FaQuoteLeft className="text-3xl" />
              </div>
              
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-[#4299f5] to-[#2d7dd1] rounded-full flex items-center justify-center text-white text-lg font-bold">
                  AP
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 text-base" style={{fontFamily: 'Montserrat, sans-serif'}}>Paras Godara</h4>
                  <p className="text-gray-600 text-xs">Hotel (MNH) Owner, Jodhpur</p>
                </div>
              </div>
              
              <div className="flex gap-1 mb-3">
                {[...Array(5)].map((_, i) => (
                  <FaStar key={i} className="text-yellow-400 text-sm" />
                ))}
              </div>
              
              <p className="text-gray-700 leading-relaxed text-sm italic">
                "Trivana has completely transformed how we manage our hotel. The booking system is seamless, and our staff loves the easy-to-use interface. Highly recommended!"
              </p>
            </div>

            {/* Testimonial 3 */}
            <div className="bg-white rounded-xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 relative">
              <div className="absolute top-4 right-4 text-[#4299f5] opacity-20">
                <FaQuoteLeft className="text-3xl" />
              </div>
              
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-[#4299f5] to-[#2d7dd1] rounded-full flex items-center justify-center text-white text-lg font-bold">
                  VK
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 text-base" style={{fontFamily: 'Montserrat, sans-serif'}}>Vikram Kumar</h4>
                  <p className="text-gray-600 text-xs">Cafe Owner, Jaipur</p>
                </div>
              </div>
              
              <div className="flex gap-1 mb-3">
                {[...Array(5)].map((_, i) => (
                  <FaStar key={i} className="text-yellow-400 text-sm" />
                ))}
              </div>
              
              <p className="text-gray-700 leading-relaxed text-sm italic">
                "Managing inventory was always a headache until we found Trivana. The automated alerts and real-time tracking have saved us thousands. Thank you, Trivana!"
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Media Coverage Section */}
      <section className="py-12 px-4 bg-gradient-to-br from-gray-50 via-white to-blue-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-10 text-gray-900" style={{fontFamily: 'Montserrat, sans-serif'}}>
            Media Coverage
          </h2>
          
          {/* Centered Cards Grid */}
          <div className="flex justify-center items-center">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-6xl">
              
              {/* News Card 1 */}
              <div className="bg-white rounded-xl overflow-hidden shadow-2xl border border-gray-200 flex-shrink-0">
                <div className="flex flex-col h-full">
                  {/* Image Section */}
                  <div className="flex items-center justify-center bg-gray-50 p-4 h-56">
                    <img 
                      src="/images/news01.jpeg" 
                      alt="Trivana Hospitality Media Coverage" 
                      className="w-full h-full object-contain rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                      onClick={() => openImageModal("/images/news01.jpeg")}
                    />
                  </div>
                  
                  {/* Content Section */}
                  <div className="p-6 flex flex-col flex-grow">
                    <h3 className="text-lg font-bold text-gray-900 mb-2" style={{fontFamily: 'Montserrat, sans-serif'}}>
                      🌟 Dainik Bhaskar Feature
                    </h3>
                    <p className="text-xs text-[#4299f5] font-semibold mb-3">September 15, 2025</p>
                    <p className="text-gray-700 leading-relaxed text-sm line-clamp-6">
                      We are delighted to share that our startup Trivana Hospitality LLP has been featured in Dainik Bhaskar Group (दैनिक भास्कर) Rajasthan today. This recognition reflects months of hard work and the courage to innovate. We feel honored to be recognized by iStart Rajasthan with a grant of ₹2.40 lakh.
                    </p>
                  </div>
                </div>
              </div>

              {/* DPIIT Certificate Card */}
              <div className="bg-white rounded-xl overflow-hidden shadow-2xl border border-gray-200 flex-shrink-0">
                <div className="flex flex-col h-full">
                  {/* Image Section */}
                  <div className="flex items-center justify-center bg-gray-50 p-4 h-56">
                    <img 
                      src="/images/news_dpiitcer.jpg" 
                      alt="Trivana Hospitality DPIIT Certificate" 
                      className="w-full h-full object-contain rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                      onClick={() => openImageModal("/images/news_dpiitcer.jpg")}
                    />
                  </div>
                  
                  {/* Content Section */}
                  <div className="p-6 flex flex-col flex-grow">
                    <h3 className="text-lg font-bold text-gray-900 mb-2" style={{fontFamily: 'Montserrat, sans-serif'}}>
                      🏆 DPIIT Recognition
                    </h3>
                    <p className="text-xs text-[#4299f5] font-semibold mb-3">Certified Startup</p>
                    <p className="text-gray-700 leading-relaxed text-sm line-clamp-6">
                      We are proud to announce that Trivana Hospitality LLP has been officially recognized by the Department for Promotion of Industry and Internal Trade (DPIIT), Government of India. This prestigious recognition validates our innovative approach to transforming the hospitality industry through technology.
                    </p>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* Video Tutorials Section */}
      <section className="py-16 px-4 pb-56 lg:pb-72 bg-white relative overflow-visible">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-extrabold mb-4 text-gray-900" style={{fontFamily: 'Montserrat, sans-serif'}}>
              📺 Video Tutorials
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-6">
              Learn how to use Trivana Merchant with our step-by-step video guides. Watch all 13 tutorials to master the platform.
            </p>
            <a 
              href="https://www.youtube.com/playlist?list=PLqmNnYv68lpNMGFyGzfiVVqz2JopF0oUG"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-red-600 text-white font-bold rounded-lg hover:bg-red-700 transition-all shadow-lg"
              style={{fontFamily: 'Montserrat, sans-serif'}}
            >
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
              </svg>
              Watch Full Playlist on YouTube
            </a>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {videoTutorials.map((video) => {
              const thumbnailUrl = `https://img.youtube.com/vi/${video.videoId}/maxresdefault.jpg`;
              
              return (
                <Link
                  key={video.id}
                  to={`/tutorials?type=${video.type}&id=${video.id}`}
                  className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 border-2 border-gray-100 hover:border-[#4299f5] group"
                >
                  <div className="relative aspect-video bg-gradient-to-br from-[#4299f5] to-[#2d7dd1] overflow-hidden">
                    <img 
                      src={thumbnailUrl}
                      alt={video.title}
                      className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                    <div className="absolute inset-0 bg-black/30 group-hover:bg-black/20 transition-all"></div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg">
                        <svg className="w-7 h-7 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M8 5v14l11-7z"/>
                        </svg>
                      </div>
                    </div>
                    <div className="absolute top-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                      {video.type === "hotel" ? "🏨 Hotel" : "🍽️ Restaurant"}
                    </div>
                    <div className="absolute top-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                      Part {video.id}
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="font-bold text-gray-900 text-sm line-clamp-2 group-hover:text-[#4299f5] transition-colors" style={{fontFamily: 'Montserrat, sans-serif'}}>
                      {video.title}
                    </h3>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* App Download Section */}
      <section className="pt-8 pb-0 px-4 bg-gradient-to-br from-[#4299f5] via-[#3a8fe5] to-[#2d7dd1] overflow-visible relative z-20 -mt-32 lg:-mt-48">
        <div className="max-w-7xl mx-auto overflow-visible">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center overflow-visible">
            {/* Left Content */}
            <div className="text-left">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-white mb-4" style={{fontFamily: 'Montserrat, sans-serif'}}>
                Download the Trivana Merchant App now for free
              </h2>
              <p className="text-base text-white/90 mb-6 leading-relaxed">
                Manage your hotel or restaurant on the go. Real-time updates, inventory tracking, and seamless operations at your fingertips.
              </p>
              <a
                href="https://play.google.com/store/apps/details?id=com.trivanahospitality.trivana_merchant_app"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 bg-white text-[#4299f5] font-bold text-base rounded-full hover:bg-gray-100 transition-all duration-300 shadow-2xl hover:shadow-3xl transform hover:scale-105"
                style={{fontFamily: 'Montserrat, sans-serif'}}
              >
                Download App
                <FaArrowRight className="text-base" />
              </a>
            </div>

            {/* Right Image - Hand with Phone */}
            <div className="flex justify-center lg:justify-end overflow-visible relative z-30">
              <div className="relative overflow-visible">
                <img 
                  src="/images/app_image.jpeg" 
                  alt="Trivana Merchant App" 
                  className="w-72 sm:w-80 md:w-96 lg:w-[26rem] h-auto object-contain rounded-2xl shadow-2xl lg:-translate-y-40"
                  style={{
                    filter: 'drop-shadow(0 25px 50px rgba(0, 0, 0, 0.3))'
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Video Modal */}
      {videoModalOpen && currentVideo && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
          onClick={closeVideoModal}
        >
          <div 
            className="relative w-full max-w-5xl bg-black rounded-xl overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={closeVideoModal}
              className="absolute top-4 right-4 z-10 w-10 h-10 bg-white/10 hover:bg-white/20 text-white rounded-full flex items-center justify-center transition-all backdrop-blur-sm"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Video Title */}
            <div className="bg-gradient-to-r from-[#4299f5] to-[#2d7dd1] px-6 py-4">
              <h3 className="text-white font-bold text-lg" style={{fontFamily: 'Montserrat, sans-serif'}}>
                {currentVideo.title}
              </h3>
            </div>

            {/* Video Player */}
            <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
              <iframe
                className="absolute top-0 left-0 w-full h-full"
                src={`https://www.youtube.com/embed/${currentVideo.videoId}?autoplay=1`}
                title={currentVideo.title}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              ></iframe>
            </div>
          </div>
        </div>
      )}

      {/* Image Modal */}
      {imageModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75 backdrop-blur-sm p-4" onClick={closeImageModal}>
          <div className="relative max-w-6xl max-h-[90vh] w-full" onClick={(e) => e.stopPropagation()}>
            {/* Close Button */}
            <button 
              onClick={closeImageModal}
              className="absolute -top-10 right-0 text-white hover:text-gray-300 transition-colors"
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            
            {/* Image */}
            <img 
              src={currentImage} 
              alt="Trivana Hospitality Media - Full View" 
              className="w-full h-auto max-h-[85vh] object-contain rounded-lg shadow-2xl"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;